import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Play, Pause, Plus, ArrowLeft, Loader2, RotateCcw, Volume2 } from "lucide-react";

const EMPTY_VOICE = { id: "", label: "", description: "", language_tags: [], sort_order: 100, active: true };

export default function AdminVoicesPage() {
  const { user, loading: authLoading } = useAuth({ redirectIfUnauthed: true });
  const navigate = useNavigate();
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);    // voice doc or {} for new
  const [isNew, setIsNew] = useState(false);
  const [deleting, setDeleting] = useState(null);  // {id, label}
  const [playingId, setPlayingId] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const audioRef = useRef(null);
  const cacheRef = useRef({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/voices");
      setVoices(r.data.voices || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load voices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.is_admin) refresh();
  }, [authLoading, user, refresh]);

  useEffect(() => () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch { /* ignore */ } }
  }, []);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) { try { audioRef.current.pause(); audioRef.current.src = ""; } catch { /* ignore */ } }
    setPlayingId(null);
  }, []);

  const playPreview = async (voiceId) => {
    if (playingId === voiceId) { stopPlayback(); return; }
    stopPlayback();
    setLoadingPreview(voiceId);
    try {
      let audio_b64 = cacheRef.current[voiceId];
      if (!audio_b64) {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voices/demo/${encodeURIComponent(voiceId)}`);
        if (!r.ok) throw new Error("Preview failed");
        const d = await r.json();
        audio_b64 = d.audio_b64;
        cacheRef.current[voiceId] = audio_b64;
      }
      const audio = new Audio(`data:audio/mpeg;base64,${audio_b64}`);
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.onerror = () => setPlayingId(null);
      await audio.play();
      setPlayingId(voiceId);
    } catch (e) {
      toast.error(e.message || "Could not preview.");
    } finally {
      setLoadingPreview(null);
    }
  };

  const startNew = () => { setEditing({ ...EMPTY_VOICE }); setIsNew(true); };
  const startEdit = (v) => { setEditing({ ...v }); setIsNew(false); };

  const saveVoice = async () => {
    const e = editing;
    if (!e.id?.trim() || !e.label?.trim()) {
      toast.error("Voice ID and label are required.");
      return;
    }
    try {
      if (isNew) {
        await api.post("/admin/voices", e);
        toast.success("Voice added");
      } else {
        await api.put(`/admin/voices/${encodeURIComponent(e.id)}`, {
          label: e.label, description: e.description, language_tags: e.language_tags,
          sort_order: e.sort_order, active: e.active,
        });
        toast.success("Voice updated");
      }
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const confirmDelete = async () => {
    const target = deleting;
    setDeleting(null);
    try {
      await api.delete(`/admin/voices/${encodeURIComponent(target.id)}`);
      toast.success(`Removed "${target.label}"`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleReset = async () => {
    setResetBusy(true);
    try {
      const r = await api.post("/admin/voices/reset", {});
      toast.success(`Seeded ${r.data.seeded} default voice${r.data.seeded === 1 ? "" : "s"} · ${r.data.active} active`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#A39A8F] flex items-center justify-center font-mono text-xs uppercase tracking-[0.25em]">
        Loading…
      </div>
    );
  }
  if (!user) return null;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
        <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
        <Navbar user={user} />
        <div className="mx-auto max-w-2xl px-6 py-32 text-center" data-testid="admin-voices-403">
          <h1 className="font-serif text-5xl mb-6">403 · Not allowed</h1>
          <p className="font-mono text-sm text-[#A39A8F] mb-8">You don't have admin access.</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">Back to dashboard</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <Helmet>
        <title>Voice catalog · Admin · AgentReply</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Navbar user={user} />

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-12 md:py-16">
        <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] mb-6 flex items-center gap-2 transition-colors">
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to dashboard
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Admin · Catalog</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Premium <span className="italic text-[#D95B38]">voice catalog</span></h1>
            <p className="font-mono text-xs text-[#A39A8F] mt-3 max-w-xl leading-relaxed">
              Active voices appear in every customer's premium voice picker and on the landing-page demo.
              Inactive voices are hidden but kept around for re-activation.
            </p>
          </div>
          <div className="flex gap-3 self-start">
            <Button onClick={handleReset} disabled={resetBusy} variant="outline"
              data-testid="reset-defaults-btn"
              className="border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#D95B38] hover:text-[#D95B38] font-mono text-xs uppercase tracking-[0.25em]">
              {resetBusy ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <RotateCcw className="w-3 h-3 mr-2" strokeWidth={1.5} />}
              Re-seed defaults
            </Button>
            <Button onClick={startNew} data-testid="add-voice-btn"
              className="bg-[#D95B38] hover:bg-[#D95B38]/90 text-[#F5F2EB] font-mono text-xs uppercase tracking-[0.25em]">
              <Plus className="w-3 h-3 mr-2" strokeWidth={1.8} /> Add voice
            </Button>
          </div>
        </div>

        <div className="border border-[#F5F2EB]/10 bg-[#1A1715]">
          {loading ? (
            <div className="p-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-[#A39A8F]">Loading…</div>
          ) : voices.length === 0 ? (
            <div className="p-16 text-center" data-testid="voices-empty">
              <Volume2 className="w-8 h-8 text-[#A39A8F] mx-auto mb-4" strokeWidth={1.2} />
              <div className="font-mono text-sm text-[#A39A8F] mb-4">Catalog is empty.</div>
              <Button onClick={handleReset} variant="outline" data-testid="seed-empty-cta">Seed default voices</Button>
            </div>
          ) : (
            <Table data-testid="voices-table">
              <TableHeader>
                <TableRow className="border-[#F5F2EB]/10 hover:bg-transparent">
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Voice</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Languages</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Order</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">Status</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {voices.map((v) => (
                  <TableRow key={v.id} className="border-[#F5F2EB]/10 hover:bg-[#221C19]" data-testid={`voice-row-${v.id}`}>
                    <TableCell className="py-4">
                      <div className="font-serif text-lg text-[#F5F2EB]">{v.label}</div>
                      <div className="font-mono text-[10px] text-[#A39A8F] mt-1">{v.description || "—"}</div>
                      <div className="font-mono text-[10px] text-[#A39A8F]/60 mt-1 truncate max-w-[280px]" title={v.id}>{v.id}</div>
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(v.language_tags || []).length === 0 && (
                          <span className="font-mono text-[10px] text-[#A39A8F]/60">none</span>
                        )}
                        {(v.language_tags || []).map((t) => (
                          <span key={t} className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/15 text-[#A39A8F] px-2 py-0.5">{t}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="py-4 font-mono text-xs text-[#A39A8F]">{v.sort_order ?? 100}</TableCell>
                    <TableCell className="py-4">
                      {v.active ? (
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-green-500/40 text-green-500 px-2 py-1">Active</span>
                      ) : (
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/15 text-[#A39A8F] px-2 py-1">Hidden</span>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => playPreview(v.id)}
                          data-testid={`preview-${v.id}`}
                          title="Play sample"
                          className="w-8 h-8 flex items-center justify-center text-[#A39A8F] hover:text-[#D95B38] hover:bg-[#0F0D0C] transition-colors"
                        >
                          {loadingPreview === v.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : playingId === v.id ? <Pause className="w-3.5 h-3.5" strokeWidth={1.8} />
                            : <Play className="w-3.5 h-3.5" strokeWidth={1.8} />}
                        </button>
                        <button
                          onClick={() => startEdit(v)}
                          data-testid={`edit-${v.id}`}
                          title="Edit"
                          className="w-8 h-8 flex items-center justify-center text-[#A39A8F] hover:text-[#F5F2EB] hover:bg-[#0F0D0C] transition-colors"
                        >
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => setDeleting({ id: v.id, label: v.label })}
                          data-testid={`delete-${v.id}`}
                          title="Delete"
                          className="w-8 h-8 flex items-center justify-center text-[#A39A8F] hover:text-[#D95B38] hover:bg-[#0F0D0C] transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Edit / create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#1A1715] border-[#F5F2EB]/15 text-[#F5F2EB] max-w-xl" data-testid="voice-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">{isNew ? "Add voice" : "Edit voice"}</DialogTitle>
            <DialogDescription className="font-mono text-xs text-[#A39A8F]">
              ElevenLabs voice_id is permanent once created. Browse the ElevenLabs library at
              {" "}<a href="https://elevenlabs.io/voice-library" target="_blank" rel="noreferrer" className="underline hover:text-[#F5F2EB]">elevenlabs.io/voice-library</a>.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5 py-3">
              <FieldRow label="Voice ID (ElevenLabs)">
                <Input
                  value={editing.id}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value.trim() })}
                  disabled={!isNew}
                  placeholder="e.g. 21m00Tcm4TlvDq8ikWAM"
                  data-testid="voice-form-id"
                  className="bg-transparent border-[#F5F2EB]/20 font-mono text-sm"
                />
              </FieldRow>
              <FieldRow label="Label">
                <Input
                  value={editing.label}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="e.g. Rachel"
                  data-testid="voice-form-label"
                  className="bg-transparent border-[#F5F2EB]/20 font-serif text-lg"
                />
              </FieldRow>
              <FieldRow label="Description">
                <Textarea
                  rows={2}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Calm, professional female"
                  data-testid="voice-form-description"
                  className="bg-transparent border-[#F5F2EB]/20 font-mono text-sm resize-none"
                />
              </FieldRow>
              <FieldRow label="Language tags (comma-separated · ISO codes like en, es, fr)">
                <Input
                  value={(editing.language_tags || []).join(", ")}
                  onChange={(e) => setEditing({
                    ...editing,
                    language_tags: e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean).slice(0, 12),
                  })}
                  placeholder="en, es, fr, de"
                  data-testid="voice-form-langs"
                  className="bg-transparent border-[#F5F2EB]/20 font-mono text-sm"
                />
              </FieldRow>
              <div className="grid grid-cols-2 gap-5">
                <FieldRow label="Sort order (lower = first)">
                  <Input
                    type="number"
                    value={editing.sort_order ?? 100}
                    onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                    data-testid="voice-form-sort"
                    className="bg-transparent border-[#F5F2EB]/20 font-mono text-sm"
                  />
                </FieldRow>
                <FieldRow label="Visibility">
                  <label className="flex items-center gap-3 mt-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                      data-testid="voice-form-active"
                      className="accent-[#D95B38]"
                    />
                    <span className="font-mono text-sm">{editing.active ? "Active (visible to customers)" : "Hidden"}</span>
                  </label>
                </FieldRow>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEditing(null)} variant="ghost"
              className="text-[#A39A8F] hover:text-[#F5F2EB] font-mono text-xs uppercase tracking-[0.25em]">Cancel</Button>
            <Button onClick={saveVoice} data-testid="voice-form-save"
              className="bg-[#D95B38] hover:bg-[#D95B38]/90 text-[#F5F2EB] font-mono text-xs uppercase tracking-[0.25em]">
              {isNew ? "Add voice" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#1A1715] border-[#F5F2EB]/15 text-[#F5F2EB]" data-testid="voice-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete "{deleting?.label}"?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-xs text-[#A39A8F]">
              This removes the voice from the catalog and disables it on every agent currently using it.
              You can re-add by ID later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#F5F2EB]/20 text-[#A39A8F] hover:bg-[#0F0D0C] hover:text-[#F5F2EB] font-mono text-xs uppercase tracking-[0.25em]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="voice-delete-confirm-btn"
              className="bg-[#D95B38] hover:bg-[#D95B38]/90 text-[#F5F2EB] font-mono text-xs uppercase tracking-[0.25em]">
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">{label}</span>
      {children}
    </label>
  );
}
